// src/services/answerBank.js
//
// What the user will say on a job application, answered once.
//
// One reader and one writer, cached like every other profile read — the Account
// page and the job-agent chat both touch these rows, and a page showing a stale
// answer after the agent just saved one is the fastest way to make somebody
// stop trusting what it says will be sent.
import { auth } from '../components/FirebaseConfig';
import { API_BASE } from '../apiSession';
import { cached, invalidate } from './queryCache';
import { factsChanged } from './aboutYouEvents';
import { openPendingTab } from '../utils/openTab';

const KEY = 'answer-bank';
const SHEET_KEY = 'answer-sheet';
// Every write can move an answer between the bank and the sheet, so both
// caches go together.
const invalidateAnswers = () => { invalidate(KEY); invalidate(SHEET_KEY); };

const authed = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in to see your answers.');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export async function fetchAnswerBank({ signal } = {}) {
  return cached(KEY, async () => {
    const res = await fetch(`${API_BASE}/api/profile/answers`, { headers: await authed(), signal });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) throw new Error(json?.error || "Couldn't load your answers.");
    return json;
  }, { ttl: 60_000 });
}

/**
 * The whole sheet: every reusable question grouped by subject, plus the
 * answers kept under a form's exact wording. One sitting answers it all.
 */
export async function fetchAnswerSheet({ signal } = {}) {
  return cached(SHEET_KEY, async () => {
    const res = await fetch(`${API_BASE}/api/profile/answer-sheet`, { headers: await authed(), signal });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) throw new Error(json?.error || "Couldn't load your answers.");
    return json;
  }, { ttl: 60_000 });
}

/**
 * Saves one answer.
 *
 * `confirm` is separate from the text on purpose: a declaration is a legal
 * statement, and affirming it has to be a thing the caller asked for rather
 * than something that happens because the field was edited.
 */
export async function saveAnswer(key, answer, { confirm = false } = {}) {
  const res = await fetch(`${API_BASE}/api/profile/answers/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: await authed(),
    body: JSON.stringify({ answer, confirm }),
    keepalive: true,
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Couldn't save that answer.");
  invalidateAnswers();
  return json;
}

/** Re-affirms a declaration without changing its text. */
export async function confirmAnswer(key) {
  const res = await fetch(`${API_BASE}/api/profile/answers/${encodeURIComponent(key)}/confirm`, {
    method: 'POST',
    headers: await authed(),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Couldn't confirm that.");
  invalidateAnswers();
  return json;
}

/**
 * "Looks right" — turns suggestions into answers, one or many at a time.
 *
 * `items` is `[{ key, answer }]`. A suggestion (the usual answer, or a draft
 * from the resume) is shown on the sheet but never filled until the person
 * confirms it (2026-09-04: verify once, never send a guess). The server
 * decides per key and returns `{ confirmed, skipped }`; a key it refuses
 * outright — the legal declarations, which never have a suggestion — fails
 * the whole call with its own sentence.
 */
export async function confirmSuggestions(items) {
  const res = await fetch(`${API_BASE}/api/profile/answers/confirm-batch`, {
    method: 'POST',
    headers: await authed(),
    body: JSON.stringify({ items }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Couldn't confirm those.");
  invalidateAnswers();
  return json;
}

/**
 * Asks the server to draft the stories, years of experience and languages
 * from the resume. What comes back are SUGGESTIONS on the sheet, tagged
 * "from your resume" — the person still confirms each one. Returns
 * `{ suggested, skipped }` as lists of keys.
 */
export async function suggestFromResume() {
  const res = await fetch(`${API_BASE}/api/profile/answer-sheet/suggest`, {
    method: 'POST',
    headers: await authed(),
    body: JSON.stringify({}),
  });
  const json = await res.json().catch(() => null);
  // A used-up month (429) says so in `message`; `error` is then a code.
  if (!res.ok || !json?.ok) throw new Error(json?.message || json?.error || "Couldn't read your resume.");
  invalidateAnswers();
  return json;
}

/**
 * What the agent would fill on one real application, field by field.
 *
 * A dry run: it reads that posting's form and resolves the saved answers onto
 * its actual controls, the same matching the extension's fill plan is built
 * from. Nothing is written and nothing is submitted.
 *
 * The point is to see the CURRENT state of both the form and the answers: a
 * person who has just answered a question and clicked back here is asking
 * exactly "did that change anything".
 */
export async function fetchFillPreview(jobId, { signal, fresh = false } = {}) {
  // The server remembers a check for a day, or until an answer changes.
  // `fresh` is the "Read the form again" link: the form is read live.
  const res = await fetch(
    `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/fill-preview${fresh ? '?fresh=1' : ''}`,
    { headers: await authed(), signal },
  );
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || "Couldn't check that application.");
  return json;
}

/**
 * Grants or revokes a standing permission: the Auto-apply switch
 * ('autoApply', and 'autoSubmit' with it). The server stamps the time.
 * Adapting a stored answer to a form's own words ('adaptAnswers') is on for
 * everyone and is asked for nowhere any more (founder, 2026-09-29); the
 * server still accepts the key, and it gates nothing.
 */
export async function setConsent(key, granted) {
  const res = await fetch(`${API_BASE}/api/profile/consent`, {
    method: 'POST',
    headers: { ...(await authed()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, granted }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.message || 'Could not save that.');
  // The bank carries the consents, and it is cached for a minute: without
  // this the Auto-apply switch flipped on the server and stayed off on the
  // page until the cache aged out (founder, 2026-09-24).
  invalidateAnswers();
  // Auto-apply on is the last of the first-run steps (Get started).
  factsChanged('consent');
  return json.consents;
}

/**
 * The review verbs for a drafted, job-pinned answer. Approval is what makes
 * the words the person's own; until then the filler refuses to send them.
 *
 * These routes are the person's. One agent tool can also approve — but only in
 * AUTO-APPLY MODE, which they switch on, and only a draft that same agent wrote
 * from material it had just read (answerTools.js passes onlyDraftedBy:'agent').
 * An answer drafted from the resume elsewhere carries a different hand and no
 * tool can reach it.
 */
export async function approveDraft(jobId, label) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/pins/approve`, {
    method: 'POST',
    headers: { ...(await authed()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not approve that.');
}

/**
 * Draft the questions this form writes for THIS application.
 *
 * "Why this company", a hard-problem essay, a skill question about this job.
 * Nothing is stored under those, deliberately — an essay written for one
 * employer must not travel to the next — so they have always come back blocked
 * with "draft it from your resume", and the only way to get one written was to
 * open the chat and ask.
 *
 * Everything this writes is a DRAFT pinned to this job and waiting for review;
 * previewFill refuses to send an unreviewed one, and approveDraft above is how
 * the words become the person's own.
 *
 * `only: 'cover_letter'` writes the cover letter and nothing else — the job
 * sheet's "Write it" (founder, 2026-09-29): the same writer the run uses,
 * not a chat turn. The letter waits for Approve like any draft.
 */
export async function draftApplicationAnswers(jobId, { only = null } = {}) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/pins/draft`, {
    method: 'POST',
    headers: { ...(await authed()), 'Content-Type': 'application/json' },
    body: JSON.stringify(only ? { only } : {}),
  });
  const json = await res.json().catch(() => null);
  // The route sits behind the AI meter, which answers a used-up month with
  // 429 and the code in `error` ('usage_limit') and the sentence in
  // `message` — so "Write it" showed "usage_limit" (review, 2026-09-29). A
  // snake_case `error` is a code; the person reads the sentence, as
  // identity.js importFailure does.
  if (!res.ok || !json?.ok) {
    const isCode = typeof json?.error === 'string' && /^[a-z]+(_[a-z]+)*$/.test(json.error);
    throw new Error((isCode ? json?.message : json?.error) || 'Could not draft these right now.');
  }
  return json;
}

export async function discardDraft(jobId, label) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/pins/discard`, {
    method: 'POST',
    headers: { ...(await authed()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not discard that.');
}

/**
 * The person's own words onto a pin. Editing is the third review verb, and
 * the strongest: saving your version IS approval, because the words are now
 * yours — the server stamps the review in the same write.
 */
export async function editPin(jobId, label, answer, { remember = true, intent = null } = {}) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/pins`, {
    method: 'POST',
    headers: { ...(await authed()), 'Content-Type': 'application/json' },
    // `remember` promotes the answer past this job — to the bank under
    // `intent` (what the form check resolved the question to), else under
    // the catalogue's match for the words, else under its exact wording.
    body: JSON.stringify({ label, answer, remember, intent }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not save that.');
  invalidateAnswers();
  return json;
}

/**
 * The development fallback, on localhost with no extension: asks the backend
 * to launch its own runner on this machine — it renders the active resume to
 * a PDF, opens a browser, and narrates into the fill feed. Resolves once the
 * launch is accepted; everything after arrives through fetchFillEvents. A
 * refusal (remote backend, run already open) comes back as a thrown sentence
 * AND as the feed's first line, so the card tells the story either way.
 */
export async function startFillRun(jobId) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/fill-run`, {
    method: 'POST',
    headers: await authed(),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not start the fill.');
}

// ── The cover letter, for one application ──────────────────────────────────
//
// One letter per queued job, never banked. A person's save arrives approved
// (editing is approval, as everywhere); the agent's drafts arrive through its
// own tool and wait for review. Skip records the decision on an optional ask.
export async function fetchCoverLetter(jobId) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/cover-letter`, {
    headers: await authed(),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not read the cover letter.');
  return json.letter;
}

export async function saveCoverLetter(jobId, body) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/cover-letter`, {
    method: 'POST',
    headers: { ...(await authed()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not save the letter.');
  return json.letter;
}

export async function approveCoverLetter(jobId) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/cover-letter/approve`, {
    method: 'POST', headers: await authed(),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not approve the letter.');
  return json.letter;
}

export async function skipCoverLetter(jobId) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/cover-letter/skip`, {
    method: 'POST', headers: await authed(),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not skip the letter.');
  return json.letter;
}

export async function discardCoverLetter(jobId) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/cover-letter/discard`, {
    method: 'POST', headers: await authed(),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not discard the letter.');
  return null;
}

/** The approved letter as the employer will receive it. Fetched with the
 *  session (a bare link cannot carry the auth header) and opened as a blob,
 *  in a tab opened inside the tap (`tab`, or one opened here before the
 *  first await): iOS refused one opened after the fetch (2026-09-30). */
export async function openCoverLetterPdf(jobId, { tab = openPendingTab() } = {}) {
  try {
    const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/cover-letter.pdf`, {
      headers: await authed(),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new Error(json?.error || 'Could not render the letter.');
    }
    const url = URL.createObjectURL(await res.blob());
    tab.show(url);
    // Revoked on a delay: the new tab needs the URL alive to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab.close();
    throw err;
  }
}

/**
 * Tells the server something happened in the browser, the way the extension
 * does. The one the page sends: `submitted` by the person — "I applied" —
 * which moves the run to submitted, the saved job to applied, and seals the
 * resume that went, exactly as a watched confirmation page would.
 */
export async function postFillEvents(jobId, events) {
  const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/fill-events`, {
    method: 'POST',
    headers: await authed(),
    body: JSON.stringify({ events }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || 'Could not record that.');
  invalidateAnswers();
  return json;
}

/**
 * The fill feed for one job: the lines the extension posts while it fills in
 * the person's browser (on localhost with no extension, the backend runner's),
 * read after `after` for the card's steps.
 */
export async function fetchFillEvents(jobId, after = 0) {
  const res = await fetch(
    `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/fill-events?after=${after}`,
    { headers: await authed() },
  );
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error('Could not read the fill feed.');
  return json;
}
